/** tm/components/ui.tsx — 공용 UI 컴포넌트 */
import React from 'react';

// ═══ 디자인 토큰 (shared.js 호환) ═══
export const C = {
  primary: '#111827', accent: '#2563EB', accentLt: '#EFF6FF',
  orange: '#EA580C', orangeLt: '#FFF7ED',
  green: '#16A34A', greenLt: '#F0FDF4',
  red: '#DC2626', redLt: '#FEF2F2',
  purple: '#7C3AED', purpleLt: '#F5F3FF',
  blue: '#2563EB', blueLt: '#EFF6FF',
  text: '#111827', sub: '#4B5563', muted: '#9CA3AF',
  bg: '#F3F4F6', bgAlt: '#FAFBFC', border: '#E5E7EB', borderDk: '#D1D5DB',
  white: '#FFFFFF', sideHover: '#1F2937',
} as const;

export const T = { xs: 11, sm: 12, md: 13, lg: 15, xl: 18 } as const;
export const R = { sm: 6, md: 10, lg: 16 } as const;

// 한글 IME 조합 중 onChange가 마지막 글자를 끊는 문제 방지 (조합이 끝나면 commit)
export function IMEText({ value, onChange, ...rest }: { value: string; onChange: (v: string) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [local, setLocal] = React.useState(value);
  const composing = React.useRef(false);
  React.useEffect(() => { if (!composing.current) setLocal(value); }, [value]);
  return <input value={local}
    onCompositionStart={() => { composing.current = true; }}
    onCompositionEnd={e => { composing.current = false; onChange(e.currentTarget.value); }}
    onChange={e => { setLocal(e.target.value); if (!composing.current) onChange(e.target.value); }}
    {...rest} />;
}
export function IMEArea({ value, onChange, ...rest }: { value: string; onChange: (v: string) => void } & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'>) {
  const [local, setLocal] = React.useState(value);
  const composing = React.useRef(false);
  React.useEffect(() => { if (!composing.current) setLocal(value); }, [value]);
  return <textarea value={local}
    onCompositionStart={() => { composing.current = true; }}
    onCompositionEnd={e => { composing.current = false; onChange(e.currentTarget.value); }}
    onChange={e => { setLocal(e.target.value); if (!composing.current) onChange(e.target.value); }}
    {...rest} />;
}

// ═══ Badge ═══
interface BadgeProps {
  children: React.ReactNode;
  bg?: string; color?: string;
  style?: React.CSSProperties;
}
export function Badge({ children, bg, color, style }: BadgeProps) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 9px', borderRadius: R.sm,
      background: bg || C.bgAlt, color: color || C.sub,
      fontSize: T.xs, fontWeight: 600, whiteSpace: 'nowrap', ...style,
    }}>{children}</span>
  );
}

// ═══ Progress Bar ═══
interface PBarProps { value: number; color?: string; h?: number }
export function PBar({ value, color, h = 5 }: PBarProps) {
  return (
    <div style={{ height: h, background: C.border, borderRadius: R.sm, overflow: 'hidden', width: '100%' }}>
      <div style={{
        height: '100%', width: `${Math.min(100, value)}%`,
        background: color || C.accent, borderRadius: R.sm, transition: 'width 0.4s',
      }} />
    </div>
  );
}

// ═══ KPI Card ═══
interface KpiCardProps {
  icon: string; label: string; value: string | number;
  color?: string; onClick?: () => void;
}
export function KpiCard({ icon, label, value, color, onClick }: KpiCardProps) {
  return (
    <div onClick={onClick} style={{
      flex: 1, padding: '16px 18px', borderRadius: R.lg,
      background: C.white, border: `1px solid ${C.border}`,
      cursor: onClick ? 'pointer' : 'default', transition: 'box-shadow 0.2s',
    }}>
      <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 6 }}>{icon} {label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: color || C.text }}>{value}</div>
    </div>
  );
}

// ═══ Search Input ═══
interface SearchInputProps {
  value: string; onChange: (v: string) => void;
  placeholder?: string; style?: React.CSSProperties;
}
export function SearchInput({ value, onChange, placeholder = '검색', style }: SearchInputProps) {
  return (
    <IMEText value={value} onChange={onChange}
      placeholder={`🔍 ${placeholder}`}
      style={{
        width: '100%', padding: '8px 12px', borderRadius: 8,
        border: `1px solid ${C.border}`, fontSize: T.sm,
        outline: 'none', boxSizing: 'border-box' as const, ...style,
      }} />
  );
}

// ═══ Filter Pill ═══
interface FilterPillProps {
  label: string; active: boolean; onClick: () => void;
  activeColor?: string;
}
export function FilterPill({ label, active, onClick, activeColor }: FilterPillProps) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 10px', borderRadius: R.sm,
      border: `1px solid ${active ? (activeColor || C.accent) : C.border}`,
      background: active ? C.accentLt : C.white,
      color: active ? (activeColor || C.accent) : C.sub,
      fontSize: T.xs, fontWeight: active ? 700 : 400, cursor: 'pointer',
    }}>{label}</button>
  );
}

// ═══ Section Card ═══
interface SectionCardProps {
  title: string; icon?: string; badge?: number; badgeColor?: string;
  children: React.ReactNode; action?: React.ReactNode;
}
export function SectionCard({ title, icon, badge, badgeColor, children, action }: SectionCardProps) {
  return (
    <div style={{
      borderRadius: R.lg, background: C.white,
      border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: 12,
    }}>
      <div style={{
        padding: '10px 14px', borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {icon && <span>{icon}</span>}
        <span style={{ fontSize: T.sm, fontWeight: 700, color: C.text, flex: 1 }}>{title}</span>
        {badge != null && badge > 0 && (
          <Badge bg={badgeColor || C.accent} color={C.white}>{badge}</Badge>
        )}
        {action}
      </div>
      {children}
    </div>
  );
}

// ═══ Empty State ═══
interface EmptyProps { icon?: string; message: string; action?: React.ReactNode }
export function Empty({ icon = '📭', message, action }: EmptyProps) {
  return (
    <div style={{ padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: T.sm, color: C.muted, marginBottom: action ? 12 : 0 }}>{message}</div>
      {action}
    </div>
  );
}

// ═══ D-Day Badge ═══
export function DDayBadge({ deadline }: { deadline: string | null }) {
  if (!deadline) return null;
  const _t=new Date(deadline);_t.setHours(0,0,0,0);const _n=new Date();_n.setHours(0,0,0,0);const diff=Math.round((_t.getTime()-_n.getTime())/86400000);
  const isOver = diff < 0;
  const isUrgent = diff >= 0 && diff <= 3;
  const isNear = diff > 3 && diff <= 7;
  const label = isOver ? `D+${Math.abs(diff)}` : diff === 0 ? 'D-Day' : `D-${diff}`;
  const bg = isOver ? C.redLt : isUrgent ? C.orangeLt : isNear ? C.accentLt : C.bgAlt;
  const color = isOver ? C.red : isUrgent ? C.orange : isNear ? C.accent : C.sub;
  return <Badge bg={bg} color={color}>{label}</Badge>;
}

// ═══ Status Badge ═══
const STATUS_COLORS: Record<string, [string, string]> = {
  '대기': [C.orangeLt, C.orange], '진행': [C.accentLt, C.accent], '진행중': [C.accentLt, C.accent],
  '완료': [C.greenLt, C.green], '보류': [C.bgAlt, C.muted],
  'FP요청대기': [C.purpleLt, C.purple],
  '중단': ['#4E5968', '#FFFFFF'], '회신대기': ['#E1F8F5', '#0A7D74'],   // 2026-10-12
  'scheduled': [C.orangeLt, C.orange], 'completed': [C.greenLt, C.green], 'sent': [C.accentLt, C.accent],
};
export function StatusBadge({ status }: { status: string }) {
  const [bg, color] = STATUS_COLORS[status] || [C.bgAlt, C.sub];
  const labels: Record<string, string> = { scheduled: '예정', completed: '완료', sent: '발송' };
  return <Badge bg={bg} color={color}>{labels[status] || status}</Badge>;
}

// ═══ 버튼 ═══
interface BtnProps {
  children: React.ReactNode; onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  disabled?: boolean; style?: React.CSSProperties;
  /** 마우스를 올리면 보이는 설명 */
  title?: string;
}
export function Btn({ children, onClick, variant = 'primary', disabled, style, title }: BtnProps) {
  const styles: Record<string, React.CSSProperties> = {
    primary: { background: `linear-gradient(135deg,${C.accent},${C.primary})`, color: C.white, border: 'none' },
    secondary: { background: C.white, color: C.text, border: `1px solid ${C.border}` },
    danger: { background: C.redLt, color: C.red, border: `1px solid ${C.red}44` },
    ghost: { background: 'transparent', color: C.sub, border: 'none' },
  };
  return (
    <button onClick={disabled ? undefined : onClick} title={title} style={{
      padding: '8px 16px', borderRadius: R.md, fontSize: T.sm, fontWeight: 600,
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
      ...styles[variant], ...style,
    }}>{children}</button>
  );
}

// ═══ Form Field ═══
interface FieldProps {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; required?: boolean;
  component?: 'input' | 'textarea' | 'select';
  options?: { value: string; label: string }[];
}
export function Field({ label, value, onChange, type = 'text', placeholder, required, component = 'input', options }: FieldProps) {
  const labelEl = (
    <label style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 3 }}>
      {label}{required && <span style={{ color: C.red }}> *</span>}
    </label>
  );
  const inputStyle: React.CSSProperties = {
    width: '100%', height: 38, padding: '9px 12px', borderRadius: 8,
    border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ marginBottom: 10 }}>
      {labelEl}
      {component === 'select' ? (
        <select value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
          <option value="">선택</option>
          {(options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : component === 'textarea' ? (
        <IMEArea value={value} onChange={onChange} placeholder={placeholder}
          rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} />
      ) : (
        <IMEText type={type} value={value} onChange={onChange}
          placeholder={placeholder} style={inputStyle} />
      )}
    </div>
  );
}

// ═══ Toast ═══
export function Toast({ message, onClose }: { message: string; onClose: () => void }) {
  React.useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      padding: '12px 24px', borderRadius: R.md, background: C.text, color: C.white,
      fontSize: T.sm, fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    }}>{message}</div>
  );
}

export const REGION_CITIES: Record<string, string[]> = {
  서울: ['강남구','강동구','강북구','강서구','관악구','광진구','구로구','금천구','노원구','도봉구','동대문구','동작구','마포구','서대문구','서초구','성동구','성북구','송파구','양천구','영등포구','용산구','은평구','종로구','중구','중랑구'],
  부산: ['강서구','금정구','기장군','남구','동구','동래구','부산진구','북구','사상구','사하구','서구','수영구','연제구','영도구','중구','해운대구'],
  대구: ['남구','달서구','달성군','동구','북구','서구','수성구','중구'],
  인천: ['강화군','계양구','남동구','동구','미추홀구','부평구','서구','연수구','옹진군','중구'],
  광주: ['광산구','남구','동구','북구','서구'],
  대전: ['대덕구','동구','서구','유성구','중구'],
  울산: ['남구','동구','북구','울주군','중구'],
  세종: ['세종시'],
  경기: ['가평군','고양시','과천시','광명시','광주시','구리시','군포시','김포시','남양주시','동두천시','부천시','성남시','수원시','시흥시','안산시','안성시','안양시','양주시','양평군','여주시','연천군','오산시','용인시','의왕시','의정부시','이천시','파주시','평택시','포천시','하남시','화성시'],
  강원: ['강릉시','고성군','동해시','삼척시','속초시','양구군','양양군','영월군','원주시','인제군','정선군','철원군','춘천시','태백시','평창군','홍천군','화천군','횡성군'],
  충북: ['괴산군','단양군','보은군','영동군','옥천군','음성군','제천시','증평군','진천군','청주시','충주시'],
  충남: ['계룡시','공주시','금산군','논산시','당진시','보령시','부여군','서산시','서천군','아산시','예산군','천안시','청양군','태안군','홍성군'],
  전북: ['고창군','군산시','김제시','남원시','무주군','부안군','순창군','완주군','익산시','임실군','장수군','전주시','정읍시','진안군'],
  전남: ['강진군','고흥군','곡성군','광양시','구례군','나주시','담양군','목포시','무안군','보성군','순천시','신안군','여수시','영광군','영암군','완도군','장성군','장흥군','진도군','함평군','해남군','화순군'],
  경북: ['경산시','경주시','고령군','구미시','군위군','김천시','문경시','봉화군','상주시','성주군','안동시','영덕군','영양군','영주시','영천시','예천군','울릉군','울진군','의성군','청도군','청송군','칠곡군','포항시'],
  경남: ['거제시','거창군','고성군','김해시','남해군','밀양시','사천시','산청군','양산시','의령군','진주시','창녕군','창원시','통영시','하동군','함안군','함양군','합천군'],
  제주: ['제주시','서귀포시'],
};

/**
 * 고치기 아이콘 단추 — 글자 링크 「고치기 · 바꾸기」 대신(2026-10-28 지도사 「고치기 바꾸기 버튼이 있는 것도 어색」)
 *  연필 하나 · 28×28 누르는 칸 · 열려 있으면 진하게 · 화면 읽기에는 label 로
 */
export function EditIcon({ label, on, onClick, disabled }: { label: string; on?: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" aria-label={label} title={label} aria-expanded={on} disabled={disabled} onClick={onClick}
      style={{ flexShrink: 0, width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 'none', borderRadius: 6, background: on ? '#EEF3F8' : 'transparent', color: on ? '#1B3A5C' : '#9CA3AF', cursor: disabled ? 'default' : 'pointer' }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
    </button>
  );
}
/** 머리 정보 한 줄 — 왼쪽 이름표(같은 폭) · 오른쪽 값 · 끝에 고치기 아이콘(있으면) · 줄 높이 28 */
export function MetaRow({ label, children, edit }: { label: string; children: React.ReactNode; edit?: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '64px minmax(0,1fr)', alignItems: 'center', minHeight: 28, fontSize: T.sm }}>
      <span style={{ color: '#6B7280' }}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, color: C.text }}>{children}{edit}</span>
    </div>
  );
}
